<template>
  <div>
    <div style="width: 100%">
      <div class="head_btn">
        <a-button
          v-if="hasClearBtn && !lockStructure"
          size="small"
          type="primary"
          @click="handleClear"
          :danger="true"
        >
          清空
        </a-button>
        <a-button
          v-if="hasDownloadBtn"
          size="small"
          type="primary"
          :loading="exportExcelLoading"
          @click="handleExportExcel"
        >
          下载导入模版
        </a-button>
        <a-button
          v-if="hasImportBtn && !lockStructure"
          size="small"
          type="primary"
          @click="handleImportExcel"
        >
          导入Excel数据
        </a-button>
        <a-button
          v-if="hasAddBtn && !lockStructure"
          size="small"
          type="primary"
          @click="handleOpenAddModal"
        >
          <template #icon>
            <plus-outlined />
          </template>
          添加一行
        </a-button>

        <template v-for="btn in field.custom_btn_list">
          <debug-code
            :action_code_id="btn.action_code_id"
            v-model:code="btn.code"
            v-model:action="btn.action"
            :options="actionTypes"
          >
            <a-button
              v-if="!lockStructure || btn.show_on_modify"
              size="small"
              :type="btn.type"
              :danger="btn.danger"
              @click="handleCustomBtnClick(btn)"
            >
              {{ btn.label }}
            </a-button>
          </debug-code>
        </template>
      </div>
      <s-table
        :dataSource="dataSource"
        :columns="columns"
        :pagination="false"
        :autoRowHeight="true"
        :bordered="true"
        stripe
        summary-fixed
        :scroll="{ x: 300 }"
        size="small"
        row-key="_key"
      >
        <template #bodyCell="{ text, column, record, index }">
          <template v-if="column.dataIndex === 'index'">
            {{ index + 1 }}
          </template>
          <template v-else-if="column.dataIndex === 'action'">
            <a-space>
              <a-button
                size="small"
                @click="handleUpdateRow(record, index)"
              >
                修改
              </a-button>
              <a-button
                v-if="!lockStructure"
                size="small"
                @click="handleRemoveRow(index)"
                danger
              >
                删除
              </a-button>
            </a-space>
          </template>
          <template v-else>
            <span>{{ text }}</span>
          </template>
        </template>
        <template
          #summary
          v-if="summaryColumns.length > 0"
        >
          <s-table-summary-row>
            <s-table-summary-cell
              v-for="item in summaryColumns"
              :key="item.index"
              :index="item.index"
            >
              <div v-if="item.index === 0">合计</div>
              <template
                v-if="item.summary"
                #default="{ total }"
              >
                <span v-if="item.unitBefore">{{ item.unitBefore }}</span>
                <span>{{ item.decimalPlaces ? total.toFixed(item.decimalPlaces) : total }}</span>
                <span v-if="item.unit">{{ item.unit }}</span>
              </template>
            </s-table-summary-cell>
          </s-table-summary-row>
        </template>
      </s-table>
    </div>
    <a-modal
      v-if="open"
      class="form-modal"
      :title="type == 'create' ? '新建' : type == 'update' ? '修改' : ''"
      width="800px"
      :open="open"
      cancelText="取消"
      okText="确定"
      centered
      @ok="handleSubmit"
      @cancel="
        () => {
          open = false;
        }
      "
    >
      <form-render-3
        ref="render"
        :form="form"
        :model="modelRef"
        :outModel="formModel"
        :options="field.options"
        :validateInfos="validateInfos"
        @update_model="onUpdateModel"
        :inner-model="_innerModel"
        :global-inner-model="globalInnerModel"
        :context="context"
        :base-key-path="itemKeyPath"
        :worker-host-functions="workerHostFunctions"
      />
    </a-modal>
  </div>
</template>

<script lang="ts">
import {
  computed,
  onMounted,
  createVNode,
  defineComponent,
  reactive,
  ref,
  h,
  watch,
  defineAsyncComponent,
} from 'vue';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { useForm } from 'ant-design-vue/es/form';
import { Modal } from 'ant-design-vue';
import modalBox from 'liyu-pc-base/components/form-modal/modal-tools';
import { exportJsonToExcel } from 'liyu-pc-base/utils/excel';
import { executeScriptInWorker } from 'liyu-pc-base/utils/function';
import debugCode from '../debugCode.vue';
import { message } from 'ant-design-vue';

export default defineComponent({
  name: 'RemoteTablePro',
  components: { PlusOutlined, debugCode },
  props: {
    field: {
      type: Object,
      required: true,
    },
    modalType: {
      type: String,
      required: true,
      default: () => '',
    },
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
    init_model: {
      type: Object,
      default: () => {},
    },
    watch: {
      type: Array,
      default: () => [],
    },
    formModel: {
      type: Object,
      required: true,
    },
    context: {
      type: Object,
      default: null,
    },
    innerModel: {
      default: null,
    },
    globalInnerModel: {
      default: null,
    },
    baseKeyPath: {
      type: Array,
      default: () => [],
    },
    workerHostFunctions: {
      type: Object,
      default: () => {},
    },
    // 修改模式传入 true：隐藏系统按钮（添加/清空/导入/行删除），只保留字段值编辑
    lockStructure: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const actionTypes = [
      {
        label: '添加(add)',
        value: 'add',
      },
      {
        label: '整体更新(update)',
        value: 'update',
      },
    ];
    const itemKeyPath = ref([]);
    const ImportExcelModal = defineAsyncComponent(
      () => import('liyu-pc-base/components/form-modal/ImportExcelModal/ImportExcelModal.vue'),
    );
    const _innerModel = computed(() => {
      if (props.innerModel) {
        if (!props.innerModel[props.field.name]) {
          props.innerModel[props.field.name] = {};
        }
        return props.innerModel[props.field.name];
      }
      return {};
    });
    const hasClearBtn = computed(() => {
      if (props.field.sys_btn_list == null) {
        return true;
      }
      if (props.field.sys_btn_list.includes('clear')) {
        return true;
      }
      return false;
    });
    const hasDownloadBtn = computed(() => {
      if (props.field.sys_btn_list == null) {
        return true;
      }
      if (props.field.sys_btn_list.includes('download')) {
        return true;
      }
      return false;
    });
    const hasImportBtn = computed(() => {
      if (props.field.sys_btn_list == null) {
        return true;
      }
      if (props.field.sys_btn_list.includes('import')) {
        return true;
      }
      return false;
    });
    const hasAddBtn = computed(() => {
      if (props.field.sys_btn_list == null) {
        return true;
      }
      if (props.field.sys_btn_list.includes('add')) {
        return true;
      }
      return false;
    });
    const columns = computed(() => {
      const list = []; //自动生成列数据
      const keys = [];
      props.field?.children?.forEach(group_item => {
        if (group_item.children instanceof Array) {
          group_item.children.forEach(i => {
            if (i.is_hide) return;
            if (keys.includes(i.name)) return;
            keys.push(i.name);
            const obj = {
              title: i.label,
              dataIndex: i.name,
              key: i.name,
              minWidth: 150,
              checked: true,
              autoWidth: true,
              resizable: true,
            };
            if (i.name2) {
              obj['dataIndex'] = i.name2;
            }
            list.push(obj);
          });
        }
      });
      const f = list.find(item => {
        return item.dataIndex == 'action';
      });
      if (!f) {
        const obj = {
          title: '操作',
          dataIndex: 'action',
          key: 'action',
          maxWidth: 120,
          width: 120,
          minWidth: 120,
          fixed: 'right',
        };
        list.push(obj);
      }
      list.splice(0, 0, {
        title: '序号',
        dataIndex: 'index',
        key: 'index',
        maxWidth: 50,
        fixed: 'left',
      });
      // summaryList [ {合计key  单位unit  保留小数位decimalPlaces等设置}]
      if (props.field?.summaryList && props.field?.summaryList instanceof Array) {
        props.field?.summaryList.forEach(item => {
          const f = list.find(i => i.dataIndex == item.key);
          if (f) {
            f.summary = true;
            if ('decimalPlaces' in item) {
              f.decimalPlaces = item.decimalPlaces;
            }
            if ('unit' in item) {
              f.unit = item.unit;
            }
            if ('unitBefore' in item) {
              f.unitBefore = item.unitBefore;
            }
          }
        });
      }
      return list;
    });
    const dataSource = computed(() => {
      const tmp_value = props.value || [];
      return [].concat(
        tmp_value.map((item, index) => {
          return { ...item, _key: index };
        }),
      );
    });
    const summaryColumns = ref([]);
    const type = ref('');
    const record_index = ref(0);
    const open = ref(false);
    const form = ref({
      settings: {
        labelAlign: 'right',
        layout: 'vertical',
      },
      dynamicFieldList: props.field.children,
    });
    const modelRef = ref({});
    const rulesRef = reactive({ ...props.field.rules });
    let validate;
    const validateInfos = ref({});
    // 记录需要忽略校验的字段
    const removeRulKeys = ref([]);
    const syncValidate = ([_modelRef, _removeRulKeys]: any[]) => {
      const new_rules_obj = {};
      Object.keys(rulesRef).forEach(key => {
        if (key in _modelRef && !_removeRulKeys[key]) {
          new_rules_obj[key] = rulesRef[key];
        }
      });
      const newRes: any = useForm(_modelRef, new_rules_obj);
      validate = newRes.validate;
      validateInfos.value = newRes.validateInfos;
    };
    const onUpdateModel = params => {
      if (params.add_key) {
        // 添加字段时，不忽略校验，所以需要从removeRulKeys中删除
        delete removeRulKeys.value[params.add_key];
        syncValidate([modelRef.value, removeRulKeys.value]);
      } else if (params.remove_key) {
        // 删除字段时，忽略校验，所以需要添加到removeRulKeys中
        removeRulKeys.value[params.remove_key] = true;
        syncValidate([modelRef.value, removeRulKeys.value]);
      } else if (params.key) {
        modelRef.value[params.key] = params.value;
        syncValidate([modelRef.value, removeRulKeys.value]);
      }
    };
    watch(
      () => modelRef.value,
      newValue => {
        syncValidate([newValue, removeRulKeys.value]);
      },
      { deep: true, immediate: true },
    );
    const handleOpenAddModal = () => {
      type.value = 'create';
      modelRef.value = {};
      props.field?.children?.forEach(row => {
        if (row?.children instanceof Array) {
          row?.children.forEach(i => {
            modelRef.value[i.name] = null;
            if (i.name2) {
              modelRef.value[i.name2] = null;
            }
          });
        }
      });
      const count = props.value?.length || 0;
      itemKeyPath.value = [...props.baseKeyPath, props.field.name, count];
      open.value = true;
    };
    const handleClear = () => {
      Modal.confirm({
        title: '确认清空吗？',
        icon: createVNode(ExclamationCircleOutlined),
        content: '清空后将无法恢复',
        okText: '确认',
        cancelText: '取消',
        onOk: () => {
          emit('update:value', []);
        },
      });
    };
    const handleRemoveRow = index => {
      Modal.confirm({
        title: `是否确认删除？`,
        icon: createVNode(ExclamationCircleOutlined),
        onOk() {
          const new_list = JSON.parse(JSON.stringify(props.value));
          new_list.splice(index, 1);
          emit('update:value', new_list);
        },
        onCancel() {},
      });
    };
    const handleUpdateRow = (record, index) => {
      type.value = 'update';
      record_index.value = index;
      modelRef.value = JSON.parse(JSON.stringify(record));
      itemKeyPath.value = [...props.baseKeyPath, props.field.name, index];
      open.value = true;
    };
    const handleSubmit = (e: Event) => {
      e.preventDefault();
      validate()
        .then(() => {
          if (type.value == 'create') {
            const new_list = JSON.parse(JSON.stringify(props.value)) || [];
            new_list.push({ ...modelRef.value });
            emit('update:value', new_list);
          } else if (type.value == 'update') {
            const new_list = JSON.parse(JSON.stringify(props.value)) || [];
            const f = new_list.find((item, i) => {
              return i === record_index.value;
            });
            if (f) {
              const new_obj = { ...modelRef.value };
              if ('_key' in new_obj) {
                delete new_obj['_key'];
              }
              Object.assign(f, new_obj);
            }
            emit('update:value', new_list);
          }
          open.value = false;
        })
        .catch(err => {
          console.log('error', err);
        });
    };
    const name2Obj: any = {};
    const handleImportExcel = () => {
      const configList: any = [];
      props.field.children.forEach((item: any) => {
        item.children.forEach((item1: any) => {
          const col = { ...item1 };
          if (item1?.name2) {
            name2Obj[item1.name] = item1?.name2;
          }
          if (['select', 'radio', 'checkbox', 'treeselect', 'cascader'].includes(item1.type)) {
            col.options = props.field.options?.[item1.name] || item1.options;
          }
          configList.push(col);
        });
      });
      const tempModal = reactive({
        open: true,
        loading: false,
        component: ImportExcelModal,
        title: '导入Excel数据',
        configList: configList,
        ok: (data: any) => {
          const err = [];
          const list = [];

          data.forEach((item: any) => {
            const obj: any = {};
            Object.keys(item).forEach((key: any) => {
              if (item[key].error) {
                err.push(h('li', item[key].error + '(' + item[key].excel_position + ')'));
              }
              obj[key] = item[key].value;
              if (name2Obj[key]) {
                obj[name2Obj[key]] = item[key].raw_value;
              }
            });
            list.push(obj);
          });
          if (err.length > 0) {
            Modal.warning({
              title: `导入Excel报错, 请改正后再尝试导入`,
              content: h('ol', err),
              icon: createVNode(ExclamationCircleOutlined),
            });
          } else {
            const newValue = [...(props.value || []), ...list];
            emit('update:value', newValue);
          }
          tempModal.open = false;
        },
        onCancel: () => {
          tempModal.open = false;
        },
      });
      modalBox(tempModal);
    };
    const exportExcelLoading = ref(false);
    const handleExportExcel = () => {
      exportExcelLoading.value = true;
      const header = [];
      const data = [];
      columns.value.forEach((item: any, index: any) => {
        if (index !== 0 && index !== columns.value.length - 1) {
          header.push(item.title);
          if (dataSource.value?.length > 0) {
            data.push(
              dataSource.value[0][item.dataIndex]
                ? dataSource.value[0][item.dataIndex].toString()
                : '',
            );
          }
        }
      });
      const percent = ref(100);
      exportJsonToExcel({
        data: [data],
        autoWidth: true,
        bookType: 'xlsx',
        filename: props.field.label,
        header: header,
        merges: [],
        multiHeader: [],
        percent: percent,
      });
      setTimeout(() => {
        exportExcelLoading.value = false;
      }, 1500);
    };
    const handleCustomBtnClick = async btn => {
      if (btn.code) {
        let res;
        try {
          res = await executeScriptInWorker(
            btn.code,
            {
              context: props.context,
              value: props.value,
              keyPath: [...props.baseKeyPath, props.field.name],
            },
            props.workerHostFunctions || {},
          );
        } catch (e) {
          message.error(e.message);
          return;
        }
        if (btn.action == 'add') {
          if (res) {
            const new_list = JSON.parse(JSON.stringify(props.value)) || [];
            new_list.push(res);
            emit('update:value', new_list);
          }
        } else if (btn.action == 'update') {
          if (res instanceof Array) {
            emit('update:value', res);
          }
        }
      }
    };
    onMounted(() => {
      Object.keys(rulesRef).forEach((key: any) => {
        modelRef.value[key] = null;
      });
    });
    watch(
      () => columns.value,
      () => {
        const list = columns.value
          .filter((item: any) => {
            if (item.checked === true) {
              return true;
            } else if (item.checked === undefined) {
              return true;
            }
            return false;
          })
          .map((item: any, index: number) => {
            const obj: any = {
              index: index,
              columnKey: item.dataIndex,
              summary: item.summary,
            };
            if (obj.summary) {
              if ('decimalPlaces' in item) {
                obj.decimalPlaces = item.decimalPlaces;
              }
              if ('unit' in item) {
                obj.unit = item.unit;
              }
              if ('unitBefore' in item) {
                obj.unitBefore = item.unitBefore;
              }
            }
            return obj;
          });
        const f_is_summary = list.find(item => item.summary);
        if (f_is_summary) {
          summaryColumns.value = list;
        } else {
          summaryColumns.value = [];
        }
      },
      { deep: true },
    );
    return {
      columns,
      dataSource,
      open,
      type,
      modelRef,
      form,
      summaryColumns,
      validateInfos,
      exportExcelLoading,
      handleOpenAddModal,
      handleSubmit,
      handleRemoveRow,
      handleUpdateRow,
      onUpdateModel,
      handleImportExcel,
      handleExportExcel,
      hasAddBtn,
      hasImportBtn,
      hasDownloadBtn,
      handleCustomBtnClick,
      actionTypes,
      _innerModel,
      itemKeyPath,
      hasClearBtn,
      handleClear,
    };
  },

  data() {
    return {
      model: this.value,
    };
  },
});
</script>
<style lang="less" scoped>
.head_btn {
  display: flex;
  gap: 4px;
  justify-content: end;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
</style>
